import React, { useState, useEffect, useCallback } from 'react';
import { Download, Search, Filter, Calendar, Activity, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { auditLogs } from '../services/api';
import VersionFooter from '../components/VersionFooter';
import ExportPasswordDialog from '../components/ExportPasswordDialog';

const getDefaultDates = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0]
  };
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const defaults = getDefaultDates();
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    startDate: defaults.startDate,
    endDate: defaults.endDate,
    userId: '',
    action: '',
    resourceType: '',
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState(null);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '') params[key] = value;
      });
      const response = await auditLogs.getAll(params);
      setLogs(response.data.data.logs);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && key !== 'page' && key !== 'limit') params[key] = value;
      });
      const response = await auditLogs.getStats(params);
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const [showExportDialog, setShowExportDialog] = useState(false);
  const [pendingExportFormat, setPendingExportFormat] = useState(null);

  const handleExport = (format) => {
    setPendingExportFormat(format);
    setShowExportDialog(true);
  };

  const doExport = async (password) => {
    setShowExportDialog(false);
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && key !== 'page' && key !== 'limit') params[key] = value;
      });

      const extraHeaders = {};
      if (password) {
        extraHeaders['X-Export-Password'] = btoa(password);
      }

      const response = pendingExportFormat === 'csv'
        ? await auditLogs.exportCSV(params, extraHeaders)
        : await auditLogs.exportXML(params, extraHeaders);

      const isEncrypted = password !== null;
      const ext = isEncrypted ? '.zip' : `.${pendingExportFormat}`;
      const mimeType = isEncrypted ? 'application/zip' : (pendingExportFormat === 'csv' ? 'text/csv' : 'application/xml');

      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${Date.now()}${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting logs:', error);
      alert('Erro ao exportar logs de auditoria.');
    }
  };

  const statusLabels = {
    success: 'Sucesso',
    failure: 'Falha',
    error: 'Erro',
    unauthorized: 'Nao Autorizado',
    forbidden: 'Proibido'
  };

  const getStatusBadge = (status) => {
    const colors = {
      success: 'bg-green-100 text-green-800',
      failure: 'bg-red-100 text-red-800',
      error: 'bg-red-100 text-red-800',
      unauthorized: 'bg-yellow-100 text-yellow-800',
      forbidden: 'bg-orange-100 text-orange-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const getSuccessCount = () => {
    if (!stats?.byStatus) return 0;
    const found = stats.byStatus.find(s => s.status === 'success');
    return found ? parseInt(found.count) : 0;
  };

  const getErrorCount = () => {
    if (!stats?.byStatus) return 0;
    return stats.byStatus
      .filter(s => ['error', 'failure', 'unauthorized', 'forbidden'].includes(s.status))
      .reduce((sum, s) => sum + parseInt(s.count), 0);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Logs de Auditoria</h1>
        <p className="text-gray-600 text-sm mt-1">Visualize e exporte logs de atividade do sistema</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Eventos</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total?.toLocaleString('pt-BR')}</p>
              </div>
              <Activity className="h-8 w-8 text-brand-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Logins Falhados</p>
                <p className="text-2xl font-bold text-red-600">{stats.security?.failedLogins || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Sucessos</p>
                <p className="text-2xl font-bold text-green-600">{getSuccessCount().toLocaleString('pt-BR')}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Erros / Falhas</p>
                <p className="text-2xl font-bold text-red-600">{getErrorCount().toLocaleString('pt-BR')}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="inline h-4 w-4 mr-1" />
              Data Inicio
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="inline h-4 w-4 mr-1" />
              Data Fim
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Filter className="inline h-4 w-4 mr-1" />
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Todos</option>
              <option value="success">Sucesso</option>
              <option value="failure">Falha</option>
              <option value="error">Erro</option>
              <option value="unauthorized">Nao Autorizado</option>
              <option value="forbidden">Proibido</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Recurso
            </label>
            <select
              value={filters.resourceType}
              onChange={(e) => handleFilterChange('resourceType', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Todos</option>
              <option value="patient">Paciente</option>
              <option value="session">Sessao</option>
              <option value="user">Usuario</option>
              <option value="security">Seguranca / LGPD</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Search className="inline h-4 w-4 mr-1" />
              Buscar
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Buscar por usuario, email, acao, IP..."
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
              />
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-brand-700 text-white rounded hover:bg-brand-800 text-sm"
              >
                Buscar
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => handleExport('csv')}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2 text-sm"
          >
            <Download className="h-4 w-4" />
            Exportar CSV
          </button>
          <button
            onClick={() => handleExport('xml')}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2 text-sm"
          >
            <Download className="h-4 w-4" />
            Exportar XML
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acao</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recurso</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">Carregando...</td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">Nenhum log encontrado</td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {new Date(log.createdAt).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {log.user?.firstName} {log.user?.lastName}
                      </div>
                      <div className="text-xs text-gray-500">{log.user?.email}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {log.action}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{log.resourceType}</div>
                      {log.resourceId && (
                        <div className="text-xs text-gray-500 font-mono">
                          {log.resourceId.substring(0, 8)}...
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {log.ipAddress || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getStatusBadge(log.status)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t">
            <div className="text-sm text-gray-700">
              Mostrando {((pagination.page - 1) * pagination.limit) + 1} ate{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
              {pagination.total} registros
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-sm">
                Pagina {pagination.page} de {pagination.totalPages}
              </span>
              <button
                onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Proxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Export Password Dialog */}
      <ExportPasswordDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        onExport={doExport}
        title={`Exportar Logs de Auditoria (${pendingExportFormat?.toUpperCase() || ''})`}
      />

      <VersionFooter />
    </div>
  );
};

export default AuditLogs;
